import { PhishingLanguagesEnum } from '@/api/enums/phishing-languages.enum';
import { client } from '@/client';
import {
  CampaignPhishingSignEnum,
  CatalogueUsageFilterEnum,
} from '@/client/campaigns';
import {
  CATALOGUE_FILTERS_INTERACTED_KEY,
  CATALOGUE_FILTERS_KEY,
} from '@/common/constants';
import { useQueryParams } from '@/hooks/query.hook';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { createUserScopedStorage } from '@/utils/localStorage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

export type CatalogueFilters = {
  search: string;
  usageFilter: CatalogueUsageFilterEnum;
  phishingSign?: CampaignPhishingSignEnum;
  language?: PhishingLanguagesEnum;
  difficulty?: number;
  skip: number;
  take: number;
};

const initialFilters: CatalogueFilters = {
  search: '',
  usageFilter: CatalogueUsageFilterEnum.SHOW_UNUSED,
  phishingSign: undefined,
  language: undefined,
  difficulty: undefined,
  skip: 0,
  take: 12,
};

const filterSchema = {
  language: (v: any) =>
    typeof v === 'string' ? (v as PhishingLanguagesEnum) : undefined,
  usageFilter: (v: any) =>
    Object.values(CatalogueUsageFilterEnum).includes(v) ? v : undefined,
  phishingSign: (v: any) =>
    Object.values(CampaignPhishingSignEnum).includes(v) ? v : undefined,
  difficulty: (v: any) => (typeof v === 'number' ? v : undefined),
  search: (v: any) => (typeof v === 'string' ? v : undefined),
};

const validateFilters = (parsed: any): Partial<CatalogueFilters> => {
  return Object.keys(filterSchema).reduce((acc, key) => {
    const validator = filterSchema[key as keyof typeof filterSchema];
    const value = validator(parsed[key]);
    return value !== undefined ? { ...acc, [key]: value } : acc;
  }, {});
};

const parseFiltersFromURL = (
  urlFilters: string | undefined,
): Partial<CatalogueFilters> => {
  if (!urlFilters) return {};

  try {
    const parsed = JSON.parse(urlFilters);
    return validateFilters(parsed);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse catalogue filters from URL:', error);
    return {};
  }
};

const getValidLanguage = (
  userLang: string | null | undefined,
  options: string[],
): PhishingLanguagesEnum | null => {
  if (userLang && options.includes(userLang)) {
    return userLang as PhishingLanguagesEnum;
  }
  if (options.includes('english')) {
    return 'english' as PhishingLanguagesEnum;
  }
  return null;
};

const buildFilterObject = (filters: CatalogueFilters) => ({
  ...(filters.language && { language: filters.language }),
  ...(filters.usageFilter !== CatalogueUsageFilterEnum.SHOW_ALL && {
    usageFilter: filters.usageFilter,
  }),
  ...(filters.phishingSign && { phishingSign: filters.phishingSign }),
  ...(filters.difficulty && { difficulty: filters.difficulty }),
});

export const useCatalogueFilters = () => {
  const { params, setSearchParams } = useQueryParams();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const storageKey = `${currentAccount?.id}-${currentUser?.id}`;

  const filtersStorage = useMemo(
    () =>
      createUserScopedStorage<CatalogueFilters>(CATALOGUE_FILTERS_KEY)(
        storageKey,
      ),
    [storageKey],
  );

  const interactedStorage = useMemo(
    () =>
      createUserScopedStorage<boolean>(CATALOGUE_FILTERS_INTERACTED_KEY)(
        storageKey,
      ),
    [storageKey],
  );

  const [hasEverInteracted, setHasEverInteracted] = useState(
    () => interactedStorage.get() ?? false,
  );

  const { data: languageOptions = [] } = useQuery({
    queryKey: ['campaign-template-languages'],
    queryFn: () => client.campaignTemplates.getCampaignTemplateLanguages(),
  });

  const getInitialFilters = useCallback((): CatalogueFilters => {
    const urlFilters = parseFiltersFromURL(params.filters);

    if (Object.keys(urlFilters).length > 0) {
      return { ...initialFilters, ...urlFilters, search: '' };
    }

    const savedFilters = filtersStorage.get();
    if (savedFilters) {
      return { ...initialFilters, ...savedFilters, search: '' };
    }

    return initialFilters;
  }, [params.filters, filtersStorage]);

  const [filters, setFilters] = useState<CatalogueFilters>(getInitialFilters);

  useEffect(() => {
    if (
      hasEverInteracted ||
      filters.language !== undefined ||
      !languageOptions?.length
    ) {
      return;
    }

    const language = getValidLanguage(currentUser?.language, languageOptions);
    if (!language) return;

    setHasEverInteracted(true);
    interactedStorage.set(true);

    const newFilters = {
      ...filters,
      language,
    };
    filtersStorage.set(newFilters);
    setFilters(newFilters);
  }, [
    currentUser?.language,
    languageOptions,
    filters.language,
    hasEverInteracted,
    filtersStorage,
    interactedStorage,
  ]);

  const updateFilter = useCallback(
    (updates: Partial<CatalogueFilters>, resetSkip = true) => {
      setHasEverInteracted(true);
      interactedStorage.set(true);

      setFilters((prev) => {
        const newFilters = {
          ...prev,
          ...updates,
          skip: resetSkip
            ? 0
            : updates.skip !== undefined
            ? updates.skip
            : prev.skip,
        };

        filtersStorage.set(newFilters);

        const filterObj = buildFilterObject(newFilters);
        setSearchParams({ filters: JSON.stringify(filterObj) });

        return newFilters;
      });
    },
    [filtersStorage, interactedStorage, setSearchParams],
  );

  const resetFilters = useCallback(() => {
    setHasEverInteracted(true);
    interactedStorage.set(true);
    filtersStorage.set(initialFilters);
    setFilters(initialFilters);
    setSearchParams({ filters: '{}' });
  }, [filtersStorage, interactedStorage, setSearchParams]);

  return {
    filters,
    updateFilter,
    resetFilters,
  };
};
